
import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { 
  Card, 
  CardContent 
} from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useSegmentData, type SegmentCategory } from "@/hooks/use-segment-data";
import { useIsMobile } from "@/hooks/use-mobile";

interface SegmentsSectionProps {
  title: string;
  subtitle: string;
}

const SegmentsSection: React.FC<SegmentsSectionProps> = ({
  title,
  subtitle
}) => {
  const { segments, categories } = useSegmentData();
  const [selectedCategory, setSelectedCategory] = useState<SegmentCategory>("Medicina");
  const [showMore, setShowMore] = useState(false);
  const isMobile = useIsMobile();

  const filteredSegments = segments.filter(segment => segment.category === selectedCategory);
  const displayedSegments = showMore ? filteredSegments : filteredSegments.slice(0, 4);

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-drcash-maastricht-blue mb-4">
            {title}
          </h2>
          <p className="text-drcash-slate-gray max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-8 max-w-6xl mx-auto">
          {/* Category Selection */}
          {isMobile ? (
            <Select value={selectedCategory} onValueChange={(value) => setSelectedCategory(value as SegmentCategory)}>
              <SelectTrigger className="w-full mb-6">
                <SelectValue placeholder="Selecione uma categoria" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category} ({segments.filter(s => s.category === category).length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="w-48 shrink-0">
              <div className="flex flex-col gap-2 sticky top-24">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={cn(
                      "text-left px-4 py-3 rounded-lg transition-all duration-200",
                      selectedCategory === category
                        ? "bg-drcash-caribbean-green/10 text-drcash-caribbean-green font-semibold"
                        : "hover:bg-gray-100 text-drcash-slate-gray"
                    )}
                  >
                    {category}
                    <span className="text-sm ml-2 opacity-60">
                      ({segments.filter(s => s.category === category).length})
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Segments Grid */}
          <div className="flex-1">
            <div className="grid md:grid-cols-2 gap-6">
              {displayedSegments.map((segment, index) => (
                <Card 
                  key={index}
                  className="transition-all duration-300 hover:shadow-md hover:-translate-y-1"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-drcash-caribbean-green/10">
                        {React.cloneElement(segment.icon, { className: "w-6 h-6 text-drcash-caribbean-green" })}
                      </div>
                      <div>
                        <h3 className="font-semibold text-drcash-maastricht-blue mb-2">{segment.name}</h3>
                        <p className="text-sm text-drcash-slate-gray">{segment.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredSegments.length > 4 && (
              <button
                onClick={() => setShowMore(!showMore)}
                className="mt-8 mx-auto flex items-center gap-2 text-drcash-caribbean-green font-medium hover:text-drcash-tiffany-blue transition-colors"
              >
                {showMore ? "Ver menos" : "Ver mais"}
                <ChevronDown className={cn(
                  "w-4 h-4 transition-transform duration-200",
                  showMore ? "rotate-180" : ""
                )} />
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SegmentsSection;
